import { defineComponent as _defineComponent } from 'vue'
import { SetRequired } from 'type-fest'
import { computed, ref, withDefaults, watch, nextTick } from 'vue'
import { useEventListener } from '@vueuse/core'

import DocumentManager from '@/components/trip/documents/manager/DocumentManager.vue'
import AccommodationMap from '@/components/trip/accommodation/AccommodationMap.vue'
import DropdownMenu from '@/components/core/DropdownMenu.vue'
import DropdownMenuItem from '@/components/core/DropdownMenuItem.vue'
import LCombobox from '@/components/core/Combobox.vue'
import LInput from '@/components/core/form/Input.vue'
import LButton from '@/components/core/Button.vue'
import DeleteModal from '@/components/core/DeleteModal.vue'
import DatePicker from '@/components/core/DatePicker.vue'
import LocationSearch from '@/components/core/LocationSearch.vue'
import EditExpenseModal from '@/components/trip/expenses/detail/EditExpenseModal.vue'
import TransportationLocationUpdateModal from '@/components/trip/accommodation/TransportationLocationUpdateModal.vue'
import { useContext } from '@/composables/context'
import { accommodationModule, expenseModule, tripModule } from '@/store'
import { Accommodation, AccommodationUpdate } from '@/types/accommodation'
import { StoredDocument, Document } from '@/types/document'
import { formattedDateRange } from '@/utils/date-utils'
import { locationToAutocompleteResult } from '@/utils/location'
import { DatePickerType } from '@/utils/enums'
import { tripQueryKeys, useSelectedTrip } from '@/composables/trip'
import { ensureFullURL, thumbnailImage } from '@/utils/utility-manager'
import { Transportation } from '@/types/transportation'
import { LocationAutocompleteResult } from '@/types/trip'
import { useUpdateTransportationsLocation } from '@/composables/transportation'
import { useQueryClient } from '@/composables/vue-query'

interface AccommodationSlideOverProps {
  value?: boolean
  accommodation?: Partial<Accommodation>
}


export default /*#__PURE__*/_defineComponent({
  __name: 'AccommodationSlideOver',
  props: {
    value: { type: Boolean, default: undefined },
    accommodation: { default: () => ({}) }
  },
  emits: ['input'],
  setup(__props: any, { emit }) {

const props = __props as { value: boolean, accommodation: Partial<Accommodation> };

const { i18n } = useContext()
const queryClient = useQueryClient()

// Props and emits -----------------------------------------




// Reactivity setup ----------------------------------------

const trip = useSelectedTrip()
const isLoading = ref(false)

const name = ref('')
const note = ref('')
const dates = ref<string[]>([])
const location = ref<LocationAutocompleteResult | undefined>()
const linkedWaypointId = ref<string | undefined>()
const documents = ref<StoredDocument[]>([])
const expenseId = ref<string | undefined>()
const href = ref('')
const favicon = ref<string | undefined>()
const hrefInputValue = ref('')
const hrefInputRef = ref<HTMLInputElement | null>(null)
const showHrefInput = ref(false)
const showWebsiteLinkMenu = ref(false)

// Computed values
const linkedWaypoint = computed(() =>
  trip.value.waypoints.find((w) => w._id === linkedWaypointId.value),
)
const expense = computed(() => {
  return expenseModule.expenses.find((e) => e._id === expenseId.value) ?? null
})

watch(
  expense,
  (newExpense) => {
    expenseModule.setCurrentExpense(newExpense)
  },
  { immediate: true },
)
const isValid = computed(() => {
  return (
    !hrefInputValue.value || URL.canParse(ensureFullURL(hrefInputValue.value))
  )
})

const isNew = computed(() => !props.accommodation._id)

// Functions -----------------------------------------------
function openHrefInput() {
  showWebsiteLinkMenu.value = false
  showHrefInput.value = true
  nextTick(() => {
    hrefInputRef.value?.focus()
  })
}

function saveHref() {
  if (!isValid.value) {
    return
  }
  href.value = hrefInputValue.value
  showHrefInput.value = false
}

function clearHref() {
  hrefInputValue.value = ''
  href.value = ''
  showHrefInput.value = false
}

function resetHref(event: KeyboardEvent) {
  event.stopPropagation()

  showHrefInput.value = false
  href.value = props.accommodation.url?.href ?? ''
  hrefInputValue.value = href.value
}

function handleWebsiteLinkButtonClick() {
  if (showWebsiteLinkMenu.value) {
    showWebsiteLinkMenu.value = false
  } else if (showHrefInput.value) {
    saveHref()
  } else if (href.value && isValid.value) {
    showWebsiteLinkMenu.value = true
  } else {
    openHrefInput()
  }
}

function handleBlur(event: FocusEvent) {
  if (event.relatedTarget && event.relatedTarget instanceof Element) {
    const targetId = event.relatedTarget.getAttribute('id')
    if (
      targetId === 'accommodation-website-link-clear' ||
      targetId === 'accommodation-website-link-button'
    ) {
      // prevent closing input from blur event when clicking on clear button or the anchor button
      return
    }
  }
  saveHref()
}

async function saveAccommodation() {
  isLoading.value = true

  const payload: AccommodationUpdate = {
    name: name.value,
    note: note.value,
    documents: documents.value.map((d) => d._id),
    expense: expense.value?._id ?? null,
  }
  if (dates.value[0]) {
    payload.checkInDate = dates.value[0]
  }
  if (dates.value[1]) {
    payload.checkOutDate = dates.value[1]
  }
  if (href.value !== props.accommodation.url?.href) {
    payload.url = href.value || null
  }

  if (isNew.value) {
    payload.location = location.value?.location
    if (linkedWaypointId.value) {
      payload.waypoint = { _id: linkedWaypointId.value }
    }
    await accommodationModule.create(payload)
  } else {
    if (!location.value) {
      payload.location = null
    } else if (location.value.id !== props.accommodation.location?.placeId) {
      payload.location = location.value.location
    }
    payload.waypoint = linkedWaypointId.value ?? null
    await accommodationModule.update({
      _id: props.accommodation._id,
      ...payload,
    })
  }
  queryClient.invalidateQueries({
    queryKey: tripQueryKeys.summary(trip.value.id),
  })

  isLoading.value = false
}

async function saveAndClose() {
  await saveAccommodation()
  handleTransportationLocationUpdate()
}

async function deleteAccommodation() {
  if (props.accommodation._id) {
    await accommodationModule.delete(props.accommodation._id)
  }

  handleTransportationLocationUpdate('delete')

  await queryClient.invalidateQueries({
    queryKey: tripQueryKeys.summary(trip.value.id),
  })
}

function linkDocument(file: Document) {
  const isStoredDocument = (f: Document): f is StoredDocument => !!f._id

  if (isStoredDocument(file)) {
    documents.value.push(file)
  }
  if (!isNew.value) {
    saveAccommodation()
  }
}

function unlinkDocument(fileId: string) {
  const index = documents.value.findIndex((d) => d._id === fileId)
  if (index === -1) {
    return
  }

  documents.value.splice(index, 1)
  if (!isNew.value) {
    saveAccommodation()
  }
}

// State management ------------------------------------------------------------

const slideOverContentRef = ref<HTMLElement | null>(null)

const internalValue = ref(props.value ?? false)
const isOpen = computed({
  get: () => props.value ?? internalValue.value,
  set: (value) => {
    internalValue.value = value
    emit('input', value)
  },
})

function initializeComponentData() {
  // Reset UI state
  showHrefInput.value = false
  showWebsiteLinkMenu.value = false

  // Initialize data from props
  name.value =
    props.accommodation.name ??
    props.accommodation.location?.name ??
    i18n.t('myAccommodation').toString()

  note.value = props.accommodation.note ?? ''

  // dates
  const initialDates: string[] = []
  if (props.accommodation.checkInDate) {
    initialDates.push(props.accommodation.checkInDate)
  }
  if (props.accommodation.checkOutDate) {
    initialDates.push(props.accommodation.checkOutDate)
  }
  dates.value = initialDates

  // location
  location.value = props.accommodation.location
    ? locationToAutocompleteResult(props.accommodation.location)
    : undefined

  // linked waypoint
  linkedWaypointId.value = props.accommodation.waypoint?._id

  // documents
  documents.value =
    props.accommodation.documents?.filter(
      (d): d is StoredDocument => !!d._id,
    ) ?? []

  // expense
  expenseId.value = props.accommodation.expense

  // website link
  href.value = props.accommodation.url?.href ?? ''
  favicon.value = props.accommodation.url?.favicon
  hrefInputValue.value = href.value
}

watch(
  isOpen,
  () => {
    if (isOpen.value) {
      initializeComponentData()
      nextTick(() => {
        slideOverContentRef.value?.focus()
      })
    }
  },
  { immediate: true },
)

// Watch location changes to update name (only if no custom name is set)
watch(location, (newValue) => {
  const hasCustomName =
    name.value.trim() &&
    name.value !== props.accommodation.location?.name &&
    name.value !== i18n.t('myAccommodation').toString()

  if (!hasCustomName) {
    if (newValue) {
      name.value = newValue.title
    } else {
      name.value = i18n.t('myAccommodation').toString()
    }
  }
})

useEventListener(slideOverContentRef, 'keydown', (event: KeyboardEvent) => {
  if (event.key !== 'Escape') {
    return
  }
  event.stopPropagation()

  emit('input', false)
})

// Transportation Location Update Functionality --------------------------------

const { mutateAsync: updateTransportationLocation } =
  useUpdateTransportationsLocation(trip.value._id)

const showTransportationUpdateModal = ref(false)
const destinationTransportationsForUpdate = ref<Transportation[]>([])
const originTransportationsForUpdate = ref<Transportation[]>([])
const newLocation = ref<LocationAutocompleteResult>()
const preselectedTransportationIds = ref<string[]>([])

async function onTransportationSelectionComplete(
  pendingTransportationUpdates: {
    transportationId: string
    fieldToUpdate: 'origin' | 'destination'
  }[],
) {
  await updateTransportationLocation({
    transportations: pendingTransportationUpdates,
    location: newLocation.value!.location,
  })

  emit('input', false)
  showTransportationUpdateModal.value = false

  await tripModule.fetch(trip.value._id)
}

function handleTransportationLocationUpdate(action?: 'delete') {
  const hasLocationChanged =
    props.accommodation.location?.placeId !== location.value?.location.placeId
  if (
    (action !== 'delete' && !hasLocationChanged) ||
    !linkedWaypoint.value ||
    !trip.value.userInfo?.useNewTransportationSystem
  ) {
    emit('input', false)
    return
  }

  const waypointIndex = trip.value.waypoints.findIndex(
    (w) => w._id === linkedWaypoint.value?._id,
  )

  const hasFlexibleRoute = (t: Transportation) =>
    ['driving', 'cycling', 'walking'].includes(t.type)

  const destinationTransportations: Transportation[] = []
  if (waypointIndex > 0) {
    destinationTransportations.push(
      ...trip.value.waypoints[waypointIndex - 1].transportations.filter(
        (t) => !t.isArrivalTransportation && hasFlexibleRoute(t),
      ),
    )
  } else {
    destinationTransportations.push(
      ...linkedWaypoint.value.transportations.filter(
        (t) => t.isArrivalTransportation && hasFlexibleRoute(t),
      ),
    )
  }

  const originTransportations = linkedWaypoint.value.transportations.filter(
    (t) => !t.isArrivalTransportation && hasFlexibleRoute(t),
  )

  destinationTransportationsForUpdate.value = destinationTransportations
  originTransportationsForUpdate.value = originTransportations
  newLocation.value =
    action !== 'delete' && location.value
      ? location.value
      : getPreferedLocationForTransportation()

  // Calculate preselected transportation IDs based on location matching
  const accommodationLocationBeforeUpdate = props.accommodation.location
  const waypointLocation = linkedWaypoint.value.location

  preselectedTransportationIds.value.push(
    ...destinationTransportations
      .filter(
        (t) =>
          t.destination.placeId ===
            accommodationLocationBeforeUpdate?.placeId ||
          t.destination.placeId === waypointLocation.placeId,
      )
      .map((t) => t._id),
  )
  preselectedTransportationIds.value.push(
    ...originTransportations
      .filter(
        (t) =>
          t.origin.placeId === accommodationLocationBeforeUpdate?.placeId ||
          t.origin.placeId === waypointLocation.placeId,
      )
      .map((t) => t._id),
  )

  if (
    destinationTransportationsForUpdate.value.length > 0 ||
    originTransportationsForUpdate.value.length > 0
  ) {
    showTransportationUpdateModal.value = true
  } else {
    emit('input', false)
  }
}

function getPreferedLocationForTransportation() {
  if (!linkedWaypoint.value) {
    return
  }

  const otherAccommodation = accommodationModule.accommodations.find(
    (a): a is SetRequired<Accommodation, 'location'> =>
      a._id !== props.accommodation._id &&
      a.waypoint?._id === linkedWaypoint.value?._id &&
      !!a.location,
  )

  return locationToAutocompleteResult(
    otherAccommodation?.location ?? linkedWaypoint.value.location,
  )
}

return { __sfc: true,i18n, queryClient, props, emit, trip, isLoading, name, note, dates, location, linkedWaypointId, documents, expenseId, href, favicon, hrefInputValue, hrefInputRef, showHrefInput, showWebsiteLinkMenu, linkedWaypoint, expense, isValid, isNew, openHrefInput, saveHref, clearHref, resetHref, handleWebsiteLinkButtonClick, handleBlur, saveAccommodation, saveAndClose, deleteAccommodation, linkDocument, unlinkDocument, slideOverContentRef, internalValue, isOpen, initializeComponentData, updateTransportationLocation, showTransportationUpdateModal, destinationTransportationsForUpdate, originTransportationsForUpdate, newLocation, preselectedTransportationIds, onTransportationSelectionComplete, handleTransportationLocationUpdate, getPreferedLocationForTransportation, DocumentManager, AccommodationMap, DropdownMenu, DropdownMenuItem, LCombobox, LInput, LButton, DeleteModal, DatePicker, LocationSearch, EditExpenseModal, TransportationLocationUpdateModal, formattedDateRange, DatePickerType, ensureFullURL, thumbnailImage }
}

})